import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { sanitizeHtml } from "@/lib/sanitize";
import {
  ArrowLeft,
  Save,
  Eye,
  EyeOff,
  Loader2,
  FileText,
  Settings,
  Image as ImageIcon,
  Undo2,
  CheckCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { articlesApi } from "@/services/api";
import type { ArticleDetail, ArticleUpdate } from "@/types";

export function ArticleEditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("content");
  const [isPreview, setIsPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Form state
  const [editedTitle, setEditedTitle] = useState("");
  const [editedMetaTitle, setEditedMetaTitle] = useState("");
  const [editedMetaDescription, setEditedMetaDescription] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedHtmlContent, setEditedHtmlContent] = useState("");
  const [editedTags, setEditedTags] = useState("");
  const [editedSlug, setEditedSlug] = useState("");

  const {
    data: article,
    isLoading,
    error,
  } = useQuery<ArticleDetail>({
    queryKey: ["article", id],
    queryFn: () => articlesApi.get(id!),
    enabled: !!id,
  });

  // Initialize form when article loads
  useEffect(() => {
    if (article) {
      setEditedTitle(article.title || "");
      setEditedMetaTitle(article.meta_title || "");
      setEditedMetaDescription(article.meta_description || "");
      setEditedContent(article.html_content || article.content || "");
      setEditedHtmlContent(article.html_content || "");
      setEditedTags(article.tags?.join(", ") || "");
      setEditedSlug(article.slug || "");
    }
  }, [article]);

  // Track unsaved changes
  useEffect(() => {
    if (article) {
      const hasChanges =
        editedTitle !== (article.title || "") ||
        editedMetaTitle !== (article.meta_title || "") ||
        editedMetaDescription !== (article.meta_description || "") ||
        editedHtmlContent !== (article.html_content || "") ||
        editedTags !== (article.tags?.join(", ") || "") ||
        editedSlug !== (article.slug || "");
      setHasUnsavedChanges(hasChanges);
    }
  }, [
    article,
    editedTitle,
    editedMetaTitle,
    editedMetaDescription,
    editedHtmlContent,
    editedTags,
    editedSlug,
  ]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const updateMutation = useMutation({
    mutationFn: (data: ArticleUpdate) => articlesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["article", id] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      setHasUnsavedChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleSave = () => {
    const tags = editedTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    updateMutation.mutate({
      title: editedTitle,
      meta_title: editedMetaTitle || undefined,
      meta_description: editedMetaDescription || undefined,
      // content = plain text for indexing; html_content = rendered HTML used
      // when publishing to WordPress. Both must be updated so added links
      // (href attrs) survive save + publish.
      content: editedContent || undefined,
      html_content: editedHtmlContent || undefined,
      tags: tags.length > 0 ? tags : undefined,
      slug: editedSlug || undefined,
    });
  };

  const handleReset = () => {
    if (article) {
      setEditedTitle(article.title || "");
      setEditedMetaTitle(article.meta_title || "");
      setEditedMetaDescription(article.meta_description || "");
      setEditedContent(article.html_content || article.content || "");
      setEditedHtmlContent(article.html_content || "");
      setEditedTags(article.tags?.join(", ") || "");
      setEditedSlug(article.slug || "");
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (confirm(t("articleEdit.unsavedChangesWarning"))) {
        navigate(`/articles/${id}`);
      }
    } else {
      navigate(`/articles/${id}`);
    }
  };

  // Calculate word count
  const wordCount = (editedHtmlContent || editedContent || "")
    .replace(/<[^>]*>/g, "")
    .split(/\s+/)
    .filter(Boolean).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate("/articles")}>
          <ArrowLeft className="h-4 w-4 me-2" />
          {t("articleDetail.back")}
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-destructive">
            {t("errors.articleNotFound")}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-background z-10 py-2 -mx-4 px-4 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 me-2" />
            {t("articleDetail.back")}
          </Button>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold truncate max-w-[300px]">
              {editedTitle || t("articleEdit.untitled")}
            </h1>
            {hasUnsavedChanges && (
              <Badge
                variant="outline"
                className="text-yellow-600 border-yellow-600"
              >
                {t("articleEdit.unsaved")}
              </Badge>
            )}
            {saveSuccess && (
              <Badge
                variant="outline"
                className="text-green-600 border-green-600"
              >
                <CheckCircle className="h-3 w-3 me-1" />
                {t("app.saved")}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {wordCount} {t("articleDetail.words")}
          </span>
          <Separator orientation="vertical" className="h-6" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPreview(!isPreview)}
          >
            {isPreview ? (
              <>
                <EyeOff className="h-4 w-4 me-2" />
                {t("articleEdit.exitPreview")}
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 me-2" />
                {t("app.preview")}
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={!hasUnsavedChanges}
          >
            <Undo2 className="h-4 w-4 me-2" />
            {t("articleEdit.reset")}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={updateMutation.isPending || !hasUnsavedChanges}
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 me-2" />
            )}
            {t("app.save")}
          </Button>
        </div>
      </div>

      {/* Error display */}
      {updateMutation.isError && (
        <Card className="border-destructive">
          <CardContent className="py-4 text-destructive">
            {t("articleEdit.failedToSave")}
          </CardContent>
        </Card>
      )}

      {isPreview ? (
        /* Preview Mode */
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{editedTitle}</CardTitle>
            <CardDescription>
              {editedMetaDescription || t("articleEdit.noDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {editedHtmlContent ? (
              <div
                className="prose prose-lg dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(editedHtmlContent),
                }}
              />
            ) : (
              <div className="whitespace-pre-wrap">{editedContent}</div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Edit Mode */
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Content Editor */}
          <div className="lg:col-span-3 space-y-4">
            {/* Title */}
            <Card>
              <CardContent className="pt-6">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  placeholder={t("articleEdit.titlePlaceholder")}
                  className="text-2xl font-bold border-0 shadow-none focus-visible:ring-0 px-0"
                />
              </CardContent>
            </Card>

            {/* WYSIWYG Editor */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {t("articleEdit.content")}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <RichTextEditor
                  content={editedContent}
                  onHtmlChange={(html) => {
                    setEditedHtmlContent(html);
                    setEditedContent(html);
                  }}
                  placeholder={t("articleEdit.contentPlaceholder")}
                  className="border-0 rounded-none min-h-[500px]"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="content" className="flex-1">
                  <Settings className="h-4 w-4 me-1" />
                  {t("articleEdit.seo")}
                </TabsTrigger>
                <TabsTrigger value="image" className="flex-1">
                  <ImageIcon className="h-4 w-4 me-1" />
                  {t("articleEdit.image")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="space-y-4 mt-4">
                {/* Meta Title */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t("articleEdit.metaTitle")}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {editedMetaTitle.length}/60{" "}
                      {t("articleDetail.meta.characters")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input
                      value={editedMetaTitle}
                      onChange={(e) => setEditedMetaTitle(e.target.value)}
                      placeholder={t("articleEdit.metaTitlePlaceholder")}
                      maxLength={60}
                    />
                  </CardContent>
                </Card>

                {/* Meta Description */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t("articleEdit.metaDescription")}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {editedMetaDescription.length}/160{" "}
                      {t("articleDetail.meta.characters")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={editedMetaDescription}
                      onChange={(e) => setEditedMetaDescription(e.target.value)}
                      placeholder={t("articleEdit.metaDescriptionPlaceholder")}
                      maxLength={160}
                      rows={3}
                    />
                  </CardContent>
                </Card>

                {/* URL Slug */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t("articleEdit.urlSlug")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input
                      value={editedSlug}
                      onChange={(e) => setEditedSlug(e.target.value)}
                      placeholder={t("articleEdit.slugPlaceholder")}
                      className="font-mono text-sm"
                      dir="ltr"
                    />
                  </CardContent>
                </Card>

                {/* Tags */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t("articleEdit.tags")}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {t("articleEdit.tagsHelp")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input
                      value={editedTags}
                      onChange={(e) => setEditedTags(e.target.value)}
                      placeholder={t("articleEdit.tagsPlaceholder")}
                    />
                    {editedTags && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {editedTags.split(",").map(
                          (tag, i) =>
                            tag.trim() && (
                              <Badge
                                key={i}
                                variant="secondary"
                                className="text-xs"
                              >
                                {tag.trim()}
                              </Badge>
                            ),
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="image" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">
                      {t("articleEdit.featuredImage")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {article.image_url ? (
                      <div className="space-y-2">
                        <img
                          src={article.image_url}
                          alt={article.title}
                          className="w-full rounded-md"
                        />
                        <p className="text-xs text-muted-foreground">
                          {t("articleEdit.imageGenerated")}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mb-2" />
                        <p className="text-sm">{t("articleEdit.noImage")}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Article Info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {t("articleEdit.info")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("articleEdit.status")}
                  </span>
                  <Badge variant="outline" className="capitalize">
                    {article.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("articleEdit.created")}
                  </span>
                  <span>
                    {new Date(article.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("articleEdit.updated")}
                  </span>
                  <span>
                    {new Date(article.updated_at).toLocaleDateString()}
                  </span>
                </div>
                {article.seo_score !== null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("articleDetail.stats.seoScore")}
                    </span>
                    <span>{article.seo_score}/100</span>
                  </div>
                )}
                {article.geo_score !== null && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t("articleDetail.stats.geoScore")}
                    </span>
                    <span>{article.geo_score}/100</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
